import { apiClient } from './client';

// GPS Device Types
export interface GPSDevice {
  imei: string;
  deviceName: string;
  deviceType: string;
  plateNumber: string | null;
  simCard: string | null;
  iccid: string | null;
  timing: {
    onlineTime: string | null;
    platformDueTime: string | null;
    activatedTime: string | null;
    userDueTime: string | null;
  };
  status: {
    isOnline: boolean;
    isActivated: boolean;
    platformExpired: boolean;
    userExpired: boolean;
  };
}

export interface VehicleTracking {
  imei: string;
  location: {
    latitude: number;
    longitude: number;
    course: number;
    speed: number;
  };
  timing: {
    gpsTime: string | null;
    heartTime: string | null;
    systemTime: string | null;
    serverTime: string | null;
  };
  status: {
    acc: 'ON' | 'OFF' | 'UNKNOWN';
    accTime: number;
    door: 'OPEN' | 'CLOSED' | 'UNKNOWN';
    charging: 'CHARGING' | 'NOT_CHARGING' | 'UNKNOWN';
    fuel: 'OK' | 'OFF' | 'UNKNOWN';
    battery: number | null;
    dataStatus: 'NEVER_ONLINE' | 'OK' | 'EXPIRED' | 'OFFLINE' | 'BLOCKED';
  };
  metrics: {
    mileage: number | null;
    todayMileage: number | null;
    externalPower: string | null;
    fuel: string | null;
    temperature: string[];
  };
}

export interface AlarmRecord {
  alarmType: number;
  alarmDescription: string;
  location: {
    longitude: number;
    latitude: number;
    speed: number;
    course: number;
  };
  timing: {
    gpsTime: string;
    systemTime: string;
    gpsTimestamp: number;
    systemTimestamp: number;
  };
  geofence: {
    id: string | null;
    name: string | null;
    isGeofenceAlarm: boolean;
  };
}

export interface Geofence {
  id: string;
  name: string;
  type: 'CIRCLE' | 'POLYGON';
  typeCode: number;
  geometry: {
    type: 'circle' | 'polygon';
    center?: { longitude: number; latitude: number };
    radius?: number;
    coordinates?: Array<{ longitude: number; latitude: number }>;
    totalPoints?: number;
  };
  speeding: {
    enabled: boolean;
    threshold: number;
    unit: string;
  };
  schedule: {
    allDay: boolean;
    timePeriods: Array<{
      start: string;
      end: string;
      duration: string;
    }>;
  };
}

export interface DeviceGeofenceBinding {
  geofenceId: string;
  geofenceName: string;
  alarms: {
    entry: { enabled: boolean; switchCode: number };
    exit: { enabled: boolean; switchCode: number };
    speeding: { enabled: boolean; switchCode: number };
    autoEngineStop: { enabled: boolean; switchCode: number };
  };
  summary: {
    totalAlarms: number;
    activeAlarms: string[];
  };
}

export interface AlarmSetting {
  alarmType: number;
  alarmDescription: string;
  enabled: boolean;
  switchCode: number;
  threshold: string | null;
  category: string;
}

// GPS API Functions
export const gpsApi = {
  // Authentication & Health
  getAccessToken: () => 
    apiClient.get<{ access_token: string }>('/gps/token'),
    
  getHealthCheck: () =>
    apiClient.get<{ service: string; status: string; timestamp: string }>('/gps/health'),

  getAccountInfo: (account: string) =>
    apiClient.get<any>(`/gps/account/${account}`),

  // Device Management
  getDeviceList: (institutionId?: string) =>
    apiClient.get<{ success: boolean; data: { devices: GPSDevice[]; totalDevices: number; account: string; institutionId?: string } }>(
      '/gps/devices',
      { params: institutionId ? { institutionId } : undefined }
    ),

  getDeviceDetails: (imeis: string[]) =>
    apiClient.get<{ success: boolean; data: { devices: GPSDevice[]; requestedIMEIs: string[] } }>('/gps/device-details', {
      params: { imeis: imeis.join(',') }
    }),

  getDeviceTracking: (imeis?: string[], institutionId?: string) =>
    apiClient.get<{ success: boolean; data: { devices: VehicleTracking[]; totalDevices: number; institutionId?: string } }>('/gps/tracking', {
      params: {
        ...(imeis && { imeis: imeis.join(',') }),
        ...(institutionId && { institutionId })
      }
    }),

  getDevicePlayback: (imei: string, begintime: number, endtime: number) =>
    apiClient.get<{
      imei: string;
      gpsPoints: Array<{
        longitude: number;
        latitude: number;
        timestamp: number;
        timestampISO: string;
        speed: number;
        course: number;
      }>;
      totalPoints: number;
    }>(`/gps/playback/${imei}`, {
      params: { begintime, endtime }
    }),

  // Alarm Management
  getDeviceAlarms: (imei: string, begintime: number, endtime: number) =>
    apiClient.get<{ imei: string; alarmRecords: AlarmRecord[]; totalAlarms: number }>('/gps/alarms-v2', {
      params: { imei, begintime, endtime }
    }),

  getDeviceAlarmSettings: (imei: string) =>
    apiClient.get<{ imei: string; alarmSettings: AlarmSetting[]; summary: any }>(`/gps/settings/${imei}`),

  modifyAlarmSettings: (imei: string, alarmtype: number, enabled: boolean, threshold: string = '') =>
    apiClient.post(`/gps/settings/${imei}`, {
      alarmtype,
      switch: enabled ? 1 : 0,
      threshold
    }),

  // Geofencing
  getGeofenceList: (pagenumber = 1, pagesize = 100) =>
    apiClient.get<{ geofences: Geofence[]; pagination: any }>('/gps/geofences', {
      params: { pagenumber, pagesize }
    }),

  createSimpleGeofence: (data: {
    imei: string;
    efencename: string;
    alarmtype: 0 | 1 | 2; // 0:out, 1:in, 2:in/out
    longitude: number;
    latitude: number;
    radius: number; // 100-5000 meters
  }) =>
    apiClient.post('/gps/geofences', data),

  createAdvancedGeofence: (data: {
    geofencename: string;
    geofencetype: 2 | 3; // 2:circle, 3:polygon
    geofenceparameter: string;
    geofencespeeding?: string; // 10-400 km/h
    geofenceusetimes?: string; // HH:mm,HH:mm;HH:mm,HH:mm
  }) =>
    apiClient.post('/gps/geofences-advanced', data),

  deleteGeofence: (geofenceId: string) =>
    apiClient.delete(`/gps/geofences/${geofenceId}`),

  getDeviceGeofences: (imei: string) =>
    apiClient.get<{ imei: string; geofenceBindings: DeviceGeofenceBinding[] }>(`/gps/devices/${imei}/geofences`),

  setDeviceGeofences: (data: {
    imei: string[];
    geofenceconfig: Array<{
      geofenceid: string;
      in: 0 | 1;
      out: 0 | 1;
      speedingalert: 0 | 1;
      relaycommand: 0 | 1;
    }>;
  }) =>
    apiClient.post('/gps/devices/geofences', data),

  // Device Control
  sendCommand: (imei: string, command: string, paramData?: string) =>
    apiClient.post<{ commandId: string; command: string }>('/gps/command', {
      imei,
      command,
      ...(paramData && { paramData })
    }),

  getCommandStatus: (commandId: string) =>
    apiClient.get(`/gps/command/${commandId}`),

  // Reports
  getMileageReport: (imeis: string[], begintime: number, endtime: number) =>
    apiClient.get<{
      devices: Array<{ imei: string; mileage: number; mileageFormatted: string }>;
      totalDevices: number;
    }>('/gps/mileage', {
      params: {
        imeis: imeis.join(','),
        begintime,
        endtime
      }
    }),

  // Account Management
  getSubCustomers: () =>
    apiClient.get<{ users: any[]; totalUsers: number }>('/gps/users'),

  blockAccount: (account: string) =>
    apiClient.post('/gps/account/block', { account }),

  unblockAccount: (account: string) =>
    apiClient.post('/gps/account/unblock', { account }),

  changePassword: (account: string, password: string) =>
    apiClient.post('/gps/users/password', { account, password })
};